/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  input,
  Input,
  OnDestroy,
  Optional,
  Output
} from '@angular/core';
import { AbstractControlValueAccessorGroup } from '@irian-ro/shared-components-module';
import { AddressDTO, PointDtoModel } from '@well/address-management-domain-model';
import { FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LocalAddressSearchService } from './local-address-search/local-address-search.service';
import { AddressUtilForm } from '@well/util-address-management';
import { CommonModule } from '@angular/common';
import { LegacyChipsComponent } from '@well/infra/legacy-primeng-components';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { KeepInScreenDirective } from './keep-in-viewport/keep-in-screen.directive';
import { LocalAddressSearchComponent } from './local-address-search/local-address-search.component';
import { GoogleAddressSearchComponent } from './google-address-search/google-address-search.component';
import { MapLocationPickerComponent } from './map-location-picker/map-location-picker.component';
import { MapsService, MapUtils } from '@irian-ro/util-maps';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Popover } from 'primeng/popover';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'well-address-search-box',
  templateUrl: './address-search-box.component.html',
  styleUrls: ['./address-search-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressSearchBoxComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressSearchBoxComponent),
      multi: true,
    }
  ],
  standalone: true,
  imports: [InputTextModule,
    CommonModule,
    FormsModule,
    LegacyChipsComponent,
    TranslateModule,
    ButtonModule,
    KeepInScreenDirective,
    LocalAddressSearchComponent,
    GoogleAddressSearchComponent,
    MapLocationPickerComponent,
    IconFieldModule,
    InputIconModule,
    Popover],
})
export class AddressSearchBoxComponent extends AbstractControlValueAccessorGroup implements OnDestroy {
  @Input() validateForDistance = true;

  allowSelection = input(true);

  @Input() searchLocal = true;

  @Input() searchOnGoolge = true;

  @Input() mapFloatRight = false;

  required = input(false);

  @Input() mapHeight: number | undefined = undefined;

  @Input() disabled = false;

  @Input() showMap = true;
  @Input() showClear = false;

  ngModel: any | undefined = undefined;

  map = toSignal(inject(MapsService).load());

  private _initialValue: any;

  get initialValue() {
    return this._initialValue;
  }

  @Input() set initialValue(value) {
    this._initialValue = value;
    this.form.patchValue(value);
  }

  @Output() addressSelect: EventEmitter<AddressDTO> = new EventEmitter<AddressDTO>();
  @Output() locationChanged = new EventEmitter<{ pointDTO: PointDtoModel }>();

  showMapDistanceError = false;

  constructor(@Optional() public localAddressSearchService: LocalAddressSearchService) {
    super();
  }

  protected createForm() {
    this.form = AddressUtilForm.createForm();
  }

  cd = inject(ChangeDetectorRef)
  onAddressSelect(event: AddressDTO, op: Popover) {
    op?.hide();
    this.cd.detectChanges();
    this.form.reset();
    this.form.patchValue(event);
    this.ngModel = '';
    this.addressSelect.next(event);
    this._initialValue = event;
  }

  onLocationChanged(event: { pointDTO: PointDtoModel }) {
    if (this.validateForDistance) {
      if (event.pointDTO != null && this._initialValue != null) {
        if (
          this.form.value.pointDTO !== null &&
          this.form.value.pointDTO.latitude !== null &&
          this.form.value.pointDTO.longitude !== null
        ) {
          this.validateDistanceBetweenAddressLocations(event.pointDTO, this._initialValue.pointDTO);
        } else {
          this.showMapDistanceError = false;
        }
      }
    }

    if (!this.validateForDistance || this.showMapDistanceError === false) {
      this.form.patchValue(event);
      this.locationChanged.next(event);
    }
  }

  private validateDistanceBetweenAddressLocations(mapPoint: PointDtoModel, formPoint: PointDtoModel) {
    const distance = MapUtils.getDistanceBetweenTwoLatLngPoints(
      mapPoint.latitude,
      mapPoint.longitude,
      formPoint.latitude,
      formPoint.longitude
    );
    this.showMapDistanceError = distance > 50;
  }

  clear() {
    this.ngModel = '';
    this.form.reset();
    this.form.value.pointDTO = null;
    this.addressSelect.next(null);
  }

  cleanNgModel() {
    this.ngModel = '';
  }
}
